/**
 * @file AnalyticsService.ts
 * @role Fire-and-forget analytics. track() records an event through a swappable sink and can
 *       NEVER throw into the caller: a broken sink, a serialization error, anything — it is
 *       all swallowed, because no analytics call may block or break gameplay (Invariant 17).
 *       The default sink logs to the dev console; production wires a real pipe.
 * @owns The sink + the bulletproof track() wrapper.
 * @reads The event name + optional props from callers.
 * @neverDoes Block gameplay. Throw. Make blocking/synchronous network calls. Decide outcomes.
 * @invariants 17 (never blocks gameplay), 8 (a notification sink, not control flow).
 * @see src/core/analytics/AnalyticsEvent.ts, docs/PLAN.md (Analytics)
 */
import type { AnalyticsEventName, AnalyticsProps, AnalyticsSink } from './AnalyticsEvent';
import { noopLogger, type Logger } from '@core/logging/Logger';

/** Dev sink: log to the console. Replace in production with a real (still non-blocking) pipe. */
export const createLoggerAnalyticsSink =
  (logger: Logger): AnalyticsSink =>
  (event) => {
    logger.info('[analytics]', event.name, event.props);
  };

export const consoleAnalyticsSink: AnalyticsSink = createLoggerAnalyticsSink(noopLogger);
export const silentAnalyticsSink: AnalyticsSink = () => {
  // Intentionally empty; useful for tests and quiet local verification.
};

export class AnalyticsService {
  constructor(private readonly sink: AnalyticsSink = consoleAnalyticsSink) {}

  /** Record an event. Swallows every error so gameplay is never affected (Invariant 17). */
  track(name: AnalyticsEventName, props: AnalyticsProps = {}): void {
    try {
      this.sink({ name, props, timestamp: new Date().toISOString() });
    } catch {
      // Analytics is fire-and-forget: a failing sink must not surface to the caller.
    }
  }
}
